import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import { FileDown, Loader2, FileSpreadsheet } from 'lucide-react';
import { downloadFile } from '../../../lib/download';
import { CandidatLink } from '../../../components/candidat/CandidatLink';
import { CompetencesProList } from '../../../components/CompetencesPro';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bloc = ({ label, children }) => children ? (
  <div><p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-0.5">{label}</p><div className="text-sm text-slate-700 whitespace-pre-wrap">{children}</div></div>
) : null;

export const FileButton = ({ url, filename, label, kind = 'PDF', size = 'sm', className = '', testId }) => {
  const [busy, setBusy] = useState(false);
  const Icon = kind === 'Excel' ? FileSpreadsheet : FileDown;
  return (
    <Button type="button" variant="outline" size={size} className={`h-7 text-[11px] ${className}`} disabled={busy} data-testid={testId}
      onClick={async () => { setBusy(true); await downloadFile(url, filename, undefined, kind); setBusy(false); }}>
      {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Icon className="w-3 h-3 mr-1" />} {label || kind}
    </Button>
  );
};
export const PdfButton = (props) => <FileButton {...props} kind="PDF" />;
export const ExcelButton = (props) => <FileButton {...props} kind="Excel" />;

export const ProgrammeDialog = ({ dossierId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  useEffect(() => { if (open) axios.get(`${API_URL}/portail/formateur/dossier/${dossierId}/programme`).then(r => setData(r.data)).catch(() => {}); }, [open, dossierId]);
  const f = data?.formation;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="programme-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
            <span>Programme — {f ? `${f.code} · ${f.titre}` : '…'}</span>
            <PdfButton url={`${API_URL}/portail/formateur/dossier/${dossierId}/programme.pdf`} filename={`programme_${f?.code || ''}.pdf`} label="Télécharger le PDF" testId="programme-pdf-btn" />
          </DialogTitle>
        </DialogHeader>
        {!data ? <p className="text-sm text-slate-400">Chargement…</p> : (
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap text-xs">
              <Badge variant="outline">{f.duree_totale_heures} h</Badge><Badge variant="outline">{f.type_formation}</Badge><Badge variant="outline">{f.type_certification}</Badge>
              {f.reference_rncp && <Badge variant="outline">RNCP {f.reference_rncp}</Badge>}
            </div>
            <Bloc label="Objectifs pédagogiques">{f.objectif_pedagogique}</Bloc>
            {(f.competences_pro?.length > 0 || f.competences_professionnelles) && <div><p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-0.5">Compétences professionnelles à valider</p><CompetencesProList competences={f.competences_pro} legacyText={f.competences_professionnelles} /></div>}
            <Bloc label="Programme">{f.programme}</Bloc>
            <div>
              <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1">Modules ({data.modules.length})</p>
              <div className="border rounded-md divide-y">
                {data.modules.map(m => (
                  <div key={m.id} className="flex items-start gap-2 px-3 py-1.5 text-xs">
                    <span className="font-mono text-violet-600 w-20 shrink-0">{m.code}</span>
                    <span className="flex-1 text-slate-800">{m.titre}</span>
                    <span className="text-slate-400 shrink-0">{m.duree_heures} h</span>
                  </div>
                ))}
              </div>
            </div>
            <Bloc label="Modalités d'organisation">{f.modalites_organisation}</Bloc>
            <Bloc label="Modalités d'évaluation">{f.modalites_evaluation}</Bloc>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ListeCandidatsDialog = ({ dossierId, dossierCode, open, onOpenChange }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { if (open) axios.get(`${API_URL}/portail/formateur/dossier/${dossierId}/liste-candidats`).then(r => setRows(r.data)).catch(() => {}); }, [open, dossierId]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="liste-candidats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
            <span>Liste des candidats ({rows?.length ?? '…'})</span>
            <span className="flex gap-1.5">
              <PdfButton url={`${API_URL}/portail/formateur/dossier/${dossierId}/liste-candidats.pdf`} filename={`liste_candidats_${dossierCode || ''}.pdf`} label="PDF" testId="liste-candidats-pdf-btn" />
              <ExcelButton url={`${API_URL}/portail/formateur/dossier/${dossierId}/liste-candidats.xlsx`} filename={`liste_candidats_${dossierCode || ''}.xlsx`} label="Excel" testId="liste-candidats-xlsx-btn" />
            </span>
          </DialogTitle>
        </DialogHeader>
        {!rows ? <p className="text-sm text-slate-400">Chargement…</p> : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500 border-b"><th className="py-1.5">Candidat</th><th>Entreprise / Financeur</th><th>Contact</th><th>Statut</th></tr></thead>
            <tbody className="divide-y">
              {rows.map(c => (
                <tr key={c.id} className={c.abandon ? 'text-slate-400' : ''}>
                  <td className="py-1.5"><CandidatLink dossierId={dossierId} candidatId={c.id}>{c.nom} {c.prenom}</CandidatLink></td>
                  <td>{c.financeur || c.entreprise || '—'}</td>
                  <td>{[c.telephone, c.email].filter(Boolean).join(' · ') || '—'}</td>
                  <td>{c.abandon ? `Abandon ${c.abandon.date}` : 'Actif'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ModuleDetailDialog = ({ module, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="module-detail-dialog">
      <DialogHeader><DialogTitle><span className="font-mono text-violet-600 mr-2">{module?.code}</span>{module?.titre}</DialogTitle></DialogHeader>
      {module && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap text-xs">
            <Badge variant="outline">{module.duree_heures} h</Badge>
            {module.sequence_name && <Badge variant="outline">{module.sequence_code} · {module.sequence_name}</Badge>}
          </div>
          <Bloc label="Objectifs">{module.objectifs}</Bloc>
          <Bloc label="Contenu">{module.contenu}</Bloc>
          <Bloc label="Déroulé pédagogique">{module.deroule_pedagogique}</Bloc>
          <Bloc label="Moyens pédagogiques">{module.moyens_pedagogiques}</Bloc>
          {module.evaluations?.length > 0 && <Bloc label="Évaluations prévues">{module.evaluations.map((e, i) => <div key={i}>• {e.type}{e.sous_type ? ` (${e.sous_type})` : ''}{e.details ? ` — ${e.details}` : ''}</div>)}</Bloc>}
          {!module.objectifs && !module.contenu && <p className="text-xs text-slate-400">Aucun détail renseigné pour ce module.</p>}
        </div>
      )}
    </DialogContent>
  </Dialog>
);
